import * as React from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Calendar as CalendarIcon } from "lucide-react";

import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const ymdToDate = (ymd) => (ymd ? new Date(`${ymd}T00:00:00`) : undefined);

// DatePicker localizado es-MX (dd/mm/yyyy). Muestra la fecha local y emite ISO 8601 UTC.
// Props: value (ISO), onChange (ISO|null), min/max ("YYYY-MM-DD"), showToday, placeholder.
export const DateField = React.forwardRef(
  ({ value, onChange, className, min, max, showToday = false, placeholder = "dd/mm/aaaa", ...props }, ref) => {
    const [open, setOpen] = React.useState(false);
    const selected = value ? new Date(value) : undefined;
    const minDate = ymdToDate(min);
    const maxDate = ymdToDate(max);

    const emit = (date) => {
      if (!date) return onChange?.(null);
      const local = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      onChange?.(local.toISOString());
      setOpen(false);
    };

    const disabled = [];
    if (minDate) disabled.push({ before: minDate });
    if (maxDate) disabled.push({ after: maxDate });

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            ref={ref}
            data-testid={props["data-testid"]}
            className={cn(
              "inline-flex items-center gap-2 px-3 text-left text-sm border border-input bg-transparent",
              !selected && "text-muted-foreground",
              className
            )}
          >
            <CalendarIcon className="h-4 w-4 opacity-70 shrink-0" />
            <span className="truncate">{selected ? format(selected, "dd/MM/yyyy", { locale: es }) : placeholder}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            locale={es}
            selected={selected}
            onSelect={emit}
            disabled={disabled.length ? disabled : undefined}
            defaultMonth={selected}
            initialFocus
          />
          {showToday && (
            <div className="border-t p-2 flex justify-end">
              <button
                type="button"
                onClick={() => emit(new Date())}
                data-testid="datefield-today-btn"
                className="text-xs font-bold uppercase tracking-wider px-3 py-1 hover:bg-muted transition-colors"
              >
                Hoy
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    );
  }
);
DateField.displayName = "DateField";
